import { testExecutionsAPI } from './test-executions.api'
import type {
  ListExecutionsRequest,
  ListExecutionsResponse,
  PlaywrightExecutionDetail,
  TestHistoryRequest,
  TestHistoryResponse,
  BatchTestHistoryRequest,
  BatchTestHistoryResponse,
  StartBuildRequest,
  StartBuildResponse,
  GetBuildStatusResponse,
  StopBuildResponse,
  ListBuildsRequest,
  ListRunningBuildsResponse,
  AnalyzeTestRequest,
  AnalyzeTestResponse,
  CompleteBuildStatus,
  CompleteExecutionResponse,
  ListTestFilesResponse,
  GetEnvironmentInfoResponse
} from './test-executions.types'

/**
 * Test executions service composable — business logic layer.
 *
 * Pure async functions wrapping testExecutionsAPI with data access.
 * No reactive state (loading/error) here — that's handled by the
 * consuming view or a future cached layer (Pinia Colada).
 *
 * View → test-executions.composable.ts → test-executions.api.ts
 */
export function useTestExecutionsService() {
  /**
   * List Playwright test executions with filtering and pagination.
   */
  const listPlaywrightExecutions = async (
    params: ListExecutionsRequest
  ): Promise<ListExecutionsResponse> => {
    return testExecutionsAPI.listPlaywrightExecutions(params)
  }

  /**
   * Get Playwright test execution details by ID.
   */
  const getPlaywrightExecution = async (
    executionId: string
  ): Promise<PlaywrightExecutionDetail> => {
    return testExecutionsAPI.getPlaywrightExecution(executionId)
  }

  /**
   * Get test execution history for a specific test.
   */
  const getTestHistory = async (params: TestHistoryRequest): Promise<TestHistoryResponse> => {
    return testExecutionsAPI.getTestHistory(params)
  }

  /**
   * Get test execution history for multiple tests in a single batch request.
   */
  const getBatchTestHistory = async (
    params: BatchTestHistoryRequest
  ): Promise<BatchTestHistoryResponse> => {
    return testExecutionsAPI.getBatchTestHistory(params)
  }

  /**
   * Get presigned URL for Playwright trace file.
   */
  const getTracePresignedUrl = async (traceUrl: string): Promise<string> => {
    return testExecutionsAPI.getTracePresignedUrl(traceUrl)
  }

  /**
   * Delete a Playwright test execution.
   */
  const deletePlaywrightExecution = async (executionId: string): Promise<void> => {
    return testExecutionsAPI.deletePlaywrightExecution(executionId)
  }

  /**
   * Start a new Playwright test execution in CodeBuild.
   */
  const startBuild = async (request: StartBuildRequest): Promise<StartBuildResponse> => {
    return testExecutionsAPI.startBuild(request)
  }

  /**
   * Get the status of a CodeBuild build.
   */
  const getBuildStatus = async (
    buildId: string,
    isBatch?: boolean
  ): Promise<GetBuildStatusResponse> => {
    return testExecutionsAPI.getBuildStatus(buildId, isBatch)
  }

  /**
   * Stop a running CodeBuild build.
   */
  const stopBuild = async (buildId: string): Promise<StopBuildResponse> => {
    return testExecutionsAPI.stopBuild(buildId)
  }

  /**
   * List CodeBuild builds for tattle-playwright.
   */
  const listBuilds = async (params: ListBuildsRequest = {}): Promise<ListRunningBuildsResponse> => {
    return testExecutionsAPI.listBuilds(params)
  }

  /**
   * Analyze a failed test using AI.
   */
  const analyzeTest = async (request: AnalyzeTestRequest): Promise<AnalyzeTestResponse> => {
    return testExecutionsAPI.analyzeTest(request)
  }

  /**
   * Manually complete a Playwright test execution.
   */
  const completeExecution = async (
    executionId: string,
    environment: string,
    buildStatus: CompleteBuildStatus = 'STOPPED'
  ): Promise<CompleteExecutionResponse> => {
    return testExecutionsAPI.completeExecution(executionId, environment, buildStatus)
  }

  /**
   * List available Playwright test files from Bitbucket.
   */
  const listTestFiles = async (project?: string): Promise<ListTestFilesResponse> => {
    return testExecutionsAPI.listTestFiles(project)
  }

  /**
   * Get environment info for the current API environment.
   */
  const getEnvironmentInfo = async (): Promise<GetEnvironmentInfoResponse> => {
    return testExecutionsAPI.getEnvironmentInfo()
  }

  return {
    listPlaywrightExecutions,
    getPlaywrightExecution,
    getTestHistory,
    getBatchTestHistory,
    getTracePresignedUrl,
    deletePlaywrightExecution,
    startBuild,
    getBuildStatus,
    stopBuild,
    listBuilds,
    analyzeTest,
    completeExecution,
    listTestFiles,
    getEnvironmentInfo
  }
}
